import type { JobSummary } from '@commissionsight/sdk';
import { explainJobError, formatPeriodLong } from '@commissionsight/shared';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../api.js';
import { JobStatusBadge } from '../components/JobStatusBadge.js';
import { FILES_TABS, PageTabs } from '../components/PageTabs.js';
import {
  Card,
  ConfirmDialog,
  EmptyState,
  ICONS,
  Loading,
  SortHeader,
  useSort,
} from '../components/ui.js';
import { downloadCsv } from '../lib/csv.js';

export function Jobs() {
  const { client } = useAuth();
  const [jobs, setJobs] = useState<JobSummary[]>([]);
  const [carrierNames, setCarrierNames] = useState<Record<string, string>>({});
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    client
      .listCarriers()
      .then((r) => setCarrierNames(Object.fromEntries(r.data.map((c) => [c.id, c.name]))))
      .catch(() => {});
  }, [client]);

  useEffect(() => {
    setLoading(true);
    client
      .listJobs({ status: statusFilter || undefined, limit: 100 })
      .then((r) => setJobs(r.data))
      .finally(() => setLoading(false));
  }, [client, statusFilter]);

  // Decorate with derived sort fields (chronological period, carrier name, upload
  // time), then make the columns sortable. Spread preserves every original field
  // so the row render is unchanged. Server-capped at 100 → bounded.
  const jobRows = useMemo(
    () =>
      jobs.map((j) => ({
        ...j,
        periodOrd: j.periodYear * 12 + j.periodMonth,
        carrierName: carrierNames[j.carrierId] ?? j.carrierId,
        uploaded: j.createdAt ? new Date(j.createdAt).getTime() : 0,
      })),
    [jobs, carrierNames],
  );
  const { sorted, sortKey, sortDir, toggle } = useSort(jobRows, { key: 'periodOrd', dir: 'desc' });

  const downloadExceptions = async (jobId: string) => {
    try {
      const csv = await client.downloadExceptions(jobId);
      downloadCsv(`exceptions-${jobId}.csv`, csv);
    } catch {
      // The file may have expired (30-day retention) or already been purged.
      setNotice(
        'This exception file is no longer available — exception files are retained for 30 days.',
      );
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <PageTabs tabs={FILES_TABS} />
        <select
          className="rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 dark:border-neutral-700 dark:bg-neutral-950"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="queued">Queued</option>
          <option value="processing">Processing</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      <Card className="overflow-hidden p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-200 bg-neutral-50/80 text-left text-xs uppercase tracking-wide text-neutral-500 dark:border-neutral-800 dark:bg-neutral-800/40">
              {(() => {
                const sp = { sortKey, sortDir, onSort: toggle };
                return (
                  <>
                    <SortHeader label="Period" k="periodOrd" {...sp} className="px-5 py-3" />
                    <SortHeader label="Carrier" k="carrierName" {...sp} className="px-3" />
                    <SortHeader label="Status" k="status" {...sp} className="px-3" />
                    <th className="px-3 font-medium">Scoring</th>
                    <SortHeader label="Uploaded" k="uploaded" {...sp} className="px-3" />
                    <th className="px-5" />
                  </>
                );
              })()}
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800/70">
            {sorted.map((j) => (
              <Fragment key={j.id}>
                <tr className="group transition hover:bg-emerald-50/40 dark:hover:bg-emerald-950/20">
                  <td className="px-5 py-3 font-medium">
                    {formatPeriodLong({ year: j.periodYear, month: j.periodMonth })}
                  </td>
                  <td className="px-3">
                    <div className="flex flex-col">
                      <span>{carrierNames[j.carrierId] ?? `${j.carrierId.slice(0, 8)}…`}</span>
                      <span className="font-mono text-[11px] text-neutral-400">
                        {j.id.slice(0, 8)}
                      </span>
                    </div>
                  </td>
                  <td className="px-3">
                    <JobStatusBadge status={j.status} />
                  </td>
                  <td className="px-3">
                    {j.stats ? (
                      <span className="flex items-center gap-1.5">
                        <Count n={j.stats.green ?? 0} color="bg-emerald-500" />
                        <Count n={j.stats.yellow ?? 0} color="bg-amber-400" />
                        <Count n={j.stats.red ?? 0} color="bg-rose-500" />
                      </span>
                    ) : (
                      <span className="text-neutral-400">—</span>
                    )}
                  </td>
                  <td className="px-3 text-neutral-500">
                    {j.createdAt
                      ? new Date(j.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })
                      : '—'}
                  </td>
                  <td className="px-5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {(j.exceptionRowCount ?? 0) > 0 && (
                        <button
                          type="button"
                          onClick={() => downloadExceptions(j.id)}
                          title="Download the rejected rows (with their errors) as CSV"
                          className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-sm font-medium text-amber-700 transition hover:bg-amber-100/60 dark:text-amber-400 dark:hover:bg-amber-900/30"
                        >
                          ⚠ {j.exceptionRowCount} rejected
                        </button>
                      )}
                      <Link
                        to={`/jobs/${j.id}`}
                        className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-sm font-medium text-emerald-600 opacity-0 transition group-hover:opacity-100 hover:bg-emerald-100/60 dark:hover:bg-emerald-900/30"
                      >
                        Results →
                      </Link>
                    </div>
                  </td>
                </tr>
                {j.status === 'failed' && <FailedRow error={j.error} />}
                {(() => {
                  // `warnings` is returned by the API but intentionally absent from
                  // the public SDK type — read it via a local extension.
                  const warnings = (j as JobWithWarnings).warnings;
                  return warnings?.length ? <WarningRow warnings={warnings} /> : null;
                })()}
              </Fragment>
            ))}
          </tbody>
        </table>
        {loading && jobs.length === 0 && <Loading label="Loading jobs…" />}
        {!loading && jobs.length === 0 && (
          <EmptyState
            icon={ICONS.list}
            title="No jobs yet"
            hint="Upload a carrier statement on the Files page and it'll show up here."
          />
        )}
      </Card>

      <ConfirmDialog
        open={notice !== null}
        title="Exception file"
        message={notice ?? ''}
        confirmLabel="OK"
        onConfirm={() => setNotice(null)}
        onCancel={() => setNotice(null)}
      />
    </div>
  );
}

interface JobWarning {
  code: string;
  severity: string;
  message: string;
}
/** The API returns post-scoring warnings on a job; the public SDK type omits them. */
type JobWithWarnings = JobSummary & { warnings?: JobWarning[] | null };

/** Surface post-scoring warnings (owed-health + statement-total variance) under a job. */
function WarningRow({ warnings }: { warnings: JobWarning[] }) {
  return (
    <tr className="bg-amber-50/50 dark:bg-amber-950/10">
      <td colSpan={6} className="px-5 py-3">
        <ul className="space-y-1.5 text-sm">
          {warnings.map((w) => (
            <li key={`${w.code}-${w.message}`} className="flex items-start gap-2">
              <span className="mt-0.5 shrink-0 text-amber-500" aria-hidden="true">
                ⚠
              </span>
              <span className="min-w-0 text-amber-800 dark:text-amber-300">{w.message}</span>
            </li>
          ))}
        </ul>
      </td>
    </tr>
  );
}

function FailedRow({ error }: { error: string | null | undefined }) {
  const ex = explainJobError(error);
  if (!ex) return null;
  return (
    <tr className="bg-rose-50/50 dark:bg-rose-950/10">
      <td colSpan={6} className="px-5 py-3">
        <div className="flex items-start gap-2 text-sm">
          <span className="mt-0.5 shrink-0 text-rose-500" aria-hidden="true">
            ⚠
          </span>
          <div className="min-w-0">
            <div className="font-medium text-rose-800 dark:text-rose-300">{ex.title}</div>
            <p className="text-neutral-600 dark:text-neutral-400">{ex.userMessage}</p>
            {ex.fixes.length > 0 && (
              <ul className="mt-1 list-disc pl-5 text-xs text-neutral-500">
                {ex.fixes.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

function Count({ n, color }: { n: number; color: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-neutral-100 px-1.5 py-0.5 text-xs font-medium tabular-nums text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
      <span className={`h-1.5 w-1.5 rounded-full ${color}`} />
      {n.toLocaleString()}
    </span>
  );
}
